import { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { X, Send, Sparkles, User } from "@/components/icons";
import { useAuth } from "../lib/auth";
import Portal from "./Portal";
import PixarDoctorAvatar from "./PixarDoctorAvatar";


/**
 * EME AI — floating doctor assistant.
 * Visible across the whole system for users with role "doctor" (and admin).
 */
export default function EmeAi() {
  const { user } = useAuth();
  const loc = useLocation();
  const params = useParams();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [speakingUntil, setSpeakingUntil] = useState(0);
  const endRef = useRef();

  // Pull patient id from URL when on a patient detail page
  // Patterns: /doctor/pacientes/:id, /secretaria/pacientes/:id, /doctor/consulta/...
  const pathMatch = loc.pathname.match(/\/pacientes\/([\w-]+)/);
  const patientId = pathMatch ? pathMatch[1] : null;

  useEffect(() => {
    // auto-scroll on new messages
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  // Force re-render while "speaking" state is active
  useEffect(() => {
    if (speakingUntil === 0) return;
    const t = setTimeout(() => setSpeakingUntil(0), Math.max(0, speakingUntil - Date.now()));
    return () => clearTimeout(t);
  }, [speakingUntil]);

  if (!user || !["doctor", "admin"].includes(user.role)) return null;
  // FAB deshabilitado — el asistente ahora vive en el botón Aris del topbar.
  return null;
  // eslint-disable-next-line no-unreachable
  if (!patientId) return null;

  // Estado del avatar según lo que esté pasando en el chat
  const avatarState = loading
    ? "thinking"
    : speakingUntil > Date.now()
      ? "speaking"
      : "idle";

  const send = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", {
        question: q,
        patient_id: patientId,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
      // La doctora "habla" durante ~3.5s al recibir respuesta
      setSpeakingUntil(Date.now() + 3500);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Error al consultar ConsuDoctor AI");
      // Put back the question so user can retry
      setMessages((m) => m.slice(0, -1));
      setInput(q);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const startNew = () => {
    setMessages([]);
    setSessionId(null);
    setInput("");
  };

  return (
    <>
      {/* Floating AI button — Ema (Pixar avatar) with living animations */}
      <button
        onClick={() => setOpen(true)}
        data-testid="eme-ai-fab"
        title="Ema · Asistente personal"
        className="fixed bottom-6 right-6 z-40 hover:scale-105 transition-transform"
        style={{ background: "transparent", border: "none", padding: 0 }}
      >
        <PixarDoctorAvatar
          state={avatarState}
          size={72}
          testid="eme-ai-fab-avatar"
        />
      </button>

      {/* Side panel */}
      {open && (
        <Portal>
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-md"
            onClick={() => setOpen(false)}
          />
          <div
            data-testid="eme-ai-panel"
            className="fixed right-4 bottom-4 top-4 z-50 w-[420px] max-w-[calc(100vw-2rem)] rounded-[28px] flex flex-col overflow-hidden bg-white shadow-[0_24px_64px_-12px_rgba(0,0,0,0.22),0_4px_12px_rgba(0,0,0,0.06)]"
          >
            {/* Header — Lunio achromatic */}
            <div className="px-5 py-4 bg-black text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#01BC8E]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-extrabold text-base leading-tight">Ema</div>
                <div className="text-[11px] text-white/65">
                  {patientId
                    ? "Conectado al historial del paciente"
                    : "Asistente personal — entra a un paciente para análisis contextual"}
                </div>
              </div>
              <button
                onClick={startNew}
                title="Nueva conversación"
                data-testid="eme-ai-new"
                className="text-white text-[11px] font-bold px-3 py-1.5 rounded-xl bg-[#01BC8E] hover:bg-[#01BC8E] transition"
              >
                Nueva
              </button>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full bg-white hover:bg-white/90 text-black flex items-center justify-center transition"
                data-testid="eme-ai-close"
                title="Cerrar"
              >
                <X className="w-4 h-4 text-black" weight="bold" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-[#F5F5F5]">
              {messages.length === 0 && (
                <div className="text-sm text-black/70 text-center mt-6 px-3">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#01BC8E]/15 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-[#01BC8E]" />
                  </div>
                  <div className="font-extrabold text-black mb-2 text-base">
                    Hola, soy Ema
                  </div>
                  <div className="mb-5 text-black/60">
                    Puedo resumir historiales, proponer diagnósticos, redactar
                    borradores de receta y sugerir seguimiento.
                  </div>
                  <div className="space-y-2">
                    <SuggestionChip
                      onClick={(t) => setInput(t)}
                      text="Resume el historial de este paciente"
                    />
                    <SuggestionChip
                      onClick={(t) => setInput(t)}
                      text="¿Qué tratamiento sugieres para fiebre y tos por 3 días?"
                    />
                    <SuggestionChip
                      onClick={(t) => setInput(t)}
                      text="¿Puede tomar ibuprofeno con paracetamol?"
                    />
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <Bubble key={i} role={m.role} content={m.content} />
              ))}
              {loading && (
                <div className="flex items-center gap-2 text-black/60 text-sm px-1">
                  <Sparkles className="w-4 h-4 animate-pulse text-[#01BC8E]" />
                  Pensando…
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Input */}
            <div className="border-t border-black/[0.06] p-3 bg-white">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="Pregúntale a Ema…"
                  rows={2}
                  data-testid="eme-ai-input"
                  className="flex-1 rounded-2xl px-4 py-2.5 text-sm bg-[#F5F5F5] border border-transparent focus:border-[#01BC8E] focus:ring-2 focus:ring-[#01BC8E]/20 outline-none resize-none transition"
                />
                <button
                  onClick={send}
                  disabled={loading || !input.trim()}
                  data-testid="eme-ai-send"
                  className="w-11 h-11 rounded-2xl flex items-center justify-center bg-[#01BC8E] hover:bg-[#01BC8E] text-white disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <div className="text-[10px] text-black/45 mt-1.5 text-center">
                Ema puede equivocarse — verifica datos clínicos críticos.
              </div>
            </div>
          </div>
        </Portal>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}

function SuggestionChip({ text, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(text)}
      className="block w-full text-left text-xs px-3 py-2.5 rounded-2xl bg-white hover:bg-black/[0.04] text-black font-medium transition"
    >
      {text}
    </button>
  );
}

function Bubble({ role, content }) {
  if (role === "user") {
    return (
      <div className="flex gap-2 justify-end">
        <div className="rounded-2xl rounded-tr-md px-4 py-2.5 text-sm max-w-[85%] text-white bg-[#01BC8E]">
          {content}
        </div>
        <div className="w-7 h-7 rounded-full bg-black/10 flex items-center justify-center flex-shrink-0">
          <User className="w-3.5 h-3.5 text-black" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-2">
      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-black text-white">
        <Sparkles className="w-3.5 h-3.5 text-[#01BC8E]" />
      </div>
      <div className="rounded-2xl rounded-tl-md px-4 py-2.5 text-sm bg-white max-w-[85%] whitespace-pre-wrap text-black leading-relaxed">
        {content}
      </div>
    </div>
  );
}
